import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import { withBack } from '../../lib/backNav'
import type { DeliveryNote } from './types'

// "Source: KI23758M · BHX4 →" on Planned Outbound / Deliveries / Outbound: jumps from a note back
// to the Inbound row it was raised from (row scrolled to + notes drawer open) with a back link to here.
interface Source { devanningId: string; group: string; itemId: string }

export function useNoteSources(notes: DeliveryNote[]): Record<string, Source> {
  const [byItem, setByItem] = useState<Record<string, Source>>({})
  const wanted = useMemo(() => {
    const ids = new Set<string>()
    for (const n of notes) {
      const id = n.source_devanning_item_id || (n.item_ids || [])[0]
      if (id) ids.add(id)
    }
    return [...ids].sort().join(',')
  }, [notes])

  useEffect(() => {
    if (!wanted) { setByItem({}); return }
    supabase.from('devanning_items').select('id, devanning_id, group_name, split_no').in('id', wanted.split(','))
      .then(({ data }) => {
        const m: Record<string, Source> = {}
        for (const r of (data as { id: string; devanning_id: string; group_name: string | null; split_no: string | null }[] | null) || []) {
          m[r.id] = { devanningId: r.devanning_id, group: r.group_name || r.split_no || r.id, itemId: r.id }
        }
        setByItem(m)
      })
  }, [wanted])

  return useMemo(() => {
    const out: Record<string, Source> = {}
    for (const n of notes) {
      const id = n.source_devanning_item_id || (n.item_ids || [])[0]
      if (id && byItem[id]) out[n.id] = byItem[id]
    }
    return out
  }, [notes, byItem])
}

export function NoteSourceLink({ note, source, warehouseId, backLabel }: {
  note: DeliveryNote
  source: Source | undefined
  warehouseId: string
  backLabel: string
}) {
  const loc = useLocation()
  if (!source) return null
  // Inbound resolves ?item= to whichever row (group) contains that devanning item.
  const url = `/warehouse/${warehouseId}/inbound?item=${source.itemId}&notes=1`
  return (
    <Link to={withBack(url, { path: loc.pathname + loc.search, label: backLabel })}
      className="block text-[10px] font-normal text-gray-500 hover:text-blue-600 hover:underline whitespace-nowrap">
      Source: {note.job_ref} · {source.group} →
    </Link>
  )
}
